<template>
  <TwoColumnLayout :extra-height-offset="170">
    <template #left>
      <div
        class="tw-overflow-y-auto tw-h-full tw-pl-[15px] tw-ml-[-15px] tw-pr-2"
      >
        <div
          v-if="discussedRemarks.length === 0"
          class="tw-text-center tw-text-sf-neutral-dark-2 tw-text-sm"
        >
          {{ copy.noDiscussedMessages }}
        </div>

        <div v-else class="tw-flex tw-flex-col tw-gap-4">
          <ProposalRemark
            v-for="remark in discussedRemarks"
            :key="remark.id"
            :remark="remark"
            :proposal-id="props.meeting.proposalId!"
          />
        </div>
      </div>
    </template>
    <template #right>
      <div class="tw-bg-sf-snow-light tw-px-6 tw-py-6 tw-rounded-lg">
        <div
          v-for="(todo, index) in htmlTodos"
          :key="index"
          :data-testid="`proposed-todo-${index}`"
          class="
            tw-flex
            tw-flex-col
            tw-gap-2
            tw-rounded-lg
            tw-group
            tw-px-6
            tw-relative
            tw-bg-sf-snow-lighter
            tw-border-sf-ai-light-2
            tw-mb-4
            tw-border-2
            tw-border-solid
            tw-py-4
          "
        >
          <RemarkBody
            :id="index"
            :key="`todo-${props.meeting.proposalId!}-${index}`"
            :sticky="false"
            :recurring="false"
            :show-context="false"
            :context-text="''"
            :context-link="''"
            :author-name="authorName"
            :created-at="currentDate"
            :rendered-text="todo"
            :allowed-actions="{
              edit: true,
              openInContext: false,
            }"
            :attachments="[]"
            :prevent-bubble-unlink="true"
            :show-period="false"
            :has-check-diff="false"
            :has-draft="false"
            :read="true"
            :done="false"
            :todo="true"
            :slim="false"
          >
            <template #top-left>
              <ToDo :done="false" :can-edit="false" />
            </template>
            <template #actions>
              <RemarkActions
                :allowed-actions="{
                  updateTags: false,
                  edit: true,
                  openInContext: false,
                  remove: true,
                  toggleRead: false,
                }"
                :read="true"
                @click="(e) => handleTodoAction(e, index)"
              />
            </template>
          </RemarkBody>
        </div>
        <div
          v-if="postMeetingRemarks.length === 0 && todos.length === 0"
          class="tw-text-center tw-text-sf-neutral-dark-2 tw-text-sm"
          :class="{ 'tw-mt-4': todos.length > 0 }"
        >
          {{ copy.noMessages }}
        </div>
        <div v-else class="tw-flex tw-flex-col tw-gap-4 tw-mb-4">
          <ProposalRemark
            v-for="remark in postMeetingRemarks"
            :key="remark.id"
            :remark="remark"
            :proposal-id="props.meeting.proposalId!"
          />
        </div>
      </div>
    </template>
  </TwoColumnLayout>
</template>
<script setup lang="ts">
import { computed, toRef } from 'vue'
import { storeToRefs } from 'pinia'

import { getCurrentUserName } from '@app/adapters/silverfin'
import useI18n from '@app/shared/vue/composables/use-I18n'
import type {
  Meeting,
  TodoModalData,
} from '@app/application/client_meetings/types'

import { useProposalDetailsStore } from '@app/application/client_meetings/screens/past/stores/proposal'
import RemarkBody from '@app/shared/vue/components/molecules/remark/remark-body.vue'
import RemarkActions from '@app/shared/vue/components/molecules/remark/remark-actions.vue'
import ToDo from '@app/shared/vue/components/molecules/remark/to-do.vue'
import ProposalRemark from './proposal-remark.vue'
import TwoColumnLayout from '@app/application/client_meetings/screens/past/components/two-column-layout.vue'
import { useMeetingRemarks } from '@app/application/client_meetings/screens/past/composables/use-meeting-remarks'

interface Props {
  meeting: Meeting
}

const props = defineProps<Props>()

const emit =
  defineEmits<{
    'add-todo': [data: TodoModalData]
  }>()

const I18n = useI18n()
const copy = {
  noDiscussedMessages: I18n.t('client_meetings.past.no_discussed_messages'),
  noMessages: I18n.t('client_meetings.past.todos.no_messages'),
}

const proposalStore = useProposalDetailsStore()
const { todosByProposalId, htmlTodosByProposalId } = storeToRefs(proposalStore)

const authorName = getCurrentUserName() || ''
const currentDate = new Date().toISOString()

const todos = computed(
  () => todosByProposalId.value?.[props.meeting.proposalId!] || []
)

const htmlTodos = computed(
  () => htmlTodosByProposalId.value?.[props.meeting.proposalId!] || []
)

const { discussedRemarks, postMeetingRemarks } = useMeetingRemarks(
  toRef(props, 'meeting')
)

function handleTodoAction(action: string, index: number): void {
  if (action === 'edit') {
    emit('add-todo', { text: todos.value[index], todoIndex: index })
  } else if (action === 'remove') {
    window.sfConfirm({
      onConfirm: async () =>
        await proposalStore.deleteFollowUpAction(
          props.meeting.proposalId!,
          index
        ),
    })
  }
}
</script>
